import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardHeader, CardBody, CardTitle,
  Table, Row, Col, Button, Input, FormGroup, Label,
  Spinner, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { HorarioAsistenciaService } from "service/HorarioAsistenciaService";
import ModalHorarioAsistencia from "components/Modals/ModalHorarioAsistencia";
import CustomPagination from "components/Pagination/Pagination";

const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

const horarioService = new HorarioAsistenciaService();

function HorarioAsistencia() {
  const notificationAlertRef = useRef(null);
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [list, setList] = useState([]);
  const [listFiltered, setListFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHorario, setSelectedHorario] = useState(null);

  // Loading state
  const [loadingBuscar, setLoadingBuscar] = useState(false);

  // Confirm modal states
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ mensaje: "", onConfirm: null });

  const getHorarios = async () => {
    setLoadingBuscar(true);
    try {
      const data = await horarioService.getHorarios();
      const items = Array.isArray(data) ? data : data?.data || [];
      setList(items);
      applyFilter(items, searchQuery);
    } catch (error) {
      console.error("Error cargando horarios de asistencia:", error);
      notify("danger", "Error al cargar los horarios de asistencia");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const applyFilter = (data, query) => {
    if (!query) { setListFiltered(data); return; }
    const q = query.toLowerCase();
    setListFiltered(data.filter((item) =>
      (DIAS[item.dia] || "").toLowerCase().includes(q)
    ));
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilter(list, query);
    setCurrentPage(1);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setSelectedHorario(null);
  };

  const handleEdit = (h) => { setSelectedHorario(h); setModalOpen(true); };

  const handleDelete = async (h) => {
    setConfirmData({
      mensaje: `¿Estás seguro de eliminar el horario de ${DIAS[h.dia]}? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        setConfirmModal(false);
        try {
          const res = await horarioService.deleteHorario(h._id);
          if (res?.confirmation === "success") getHorarios();
          else notify("danger", res?.message || "Error al eliminar");
        } catch (error) {
          console.error("Error eliminando:", error);
          notify("danger", "Error al eliminar el horario");
        }
      },
    });
    setConfirmModal(true);
  };

  const indexOfLast = currentPage * itemsPerPage;
  const currentItems = listFiltered.slice(indexOfLast - itemsPerPage, indexOfLast);

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Configuración de Horarios de Asistencia</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button color="link" className="btn-icon btn-round">
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 2: Barra de filtros */}
                <Row className="align-items-end mb-4">
                  <Col md="11">
                    <FormGroup className="mb-0">
                      <Label for="search">Buscar por día</Label>
                      <Input
                        type="text"
                        id="search"
                        placeholder="Ej: Lunes, Martes..."
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ height: "40px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={getHorarios}
                        disabled={loadingBuscar}
                        style={{ height: "40px", padding: "0" }}
                        title="Buscar"
                      >
                        {loadingBuscar ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones */}
                <Row className="mb-4">
                  <Col md="6"></Col>
                  <Col md="6" className="text-right">
                    <Button
                      color="primary"
                      onClick={toggleModal}
                      className="btn-fill"
                    >
                      <i className="tim-icons icon-simple-add mr-2" />
                      Agregar Horario
                    </Button>
                  </Col>
                </Row>

                <hr className="my-4" />

                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>Día de la Semana</th>
                      <th>Inicio Mañana</th>
                      <th>Fin Mañana</th>
                      <th>Inicio Tarde</th>
                      <th>Fin Tarde</th>
                      <th>Estado</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((row, i) => (
                      <tr key={row._id || i}>
                        <td><strong>{DIAS[row.dia] || row.dia}</strong></td>
                        <td>{row.horaInicioManiana || "-"}</td>
                        <td>{row.horaFinManiana || "-"}</td>
                        <td>{row.horaInicioTarde || "-"}</td>
                        <td>{row.horaFinTarde || "-"}</td>
                        <td>
                          {row.estado === "1"
                            ? <span className="text-success">Activo</span>
                            : <span className="text-danger">Inactivo</span>}
                        </td>
                        <td className="text-right">
                          <Button color="info" size="sm" className="btn-icon btn-link" title="Editar" onClick={() => handleEdit(row)}>
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <Button color="danger" size="sm" className="btn-icon btn-link" title="Eliminar" onClick={() => handleDelete(row)}>
                            <i className="tim-icons icon-trash-simple" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {listFiltered.length === 0 && (
                      <tr><td colSpan="7" className="text-center">No se encontraron registros</td></tr>
                    )}
                  </tbody>
                </Table>
                <CustomPagination
                  itemsPerPage={itemsPerPage}
                  totalItems={listFiltered.length}
                  paginate={setCurrentPage}
                  currentPage={currentPage}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <ModalHorarioAsistencia
        isOpen={modalOpen}
        toggle={toggleModal}
        selectedHorario={selectedHorario}
        onSave={getHorarios}
      />

      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} size="sm" centered>
        <ModalHeader toggle={() => setConfirmModal(false)}>Confirmar acción</ModalHeader>
        <ModalBody><p>{confirmData.mensaje}</p></ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancelar</Button>
          <Button color="danger" onClick={confirmData.onConfirm}>Confirmar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default HorarioAsistencia;
